import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import classes from "./FilterModal.module.css";
import { findHost } from "../../../helpers/MappingSecurispot";
import i18n from "../../../i18n";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: string[];
  selectedOptions: string[];
  onChange: (selectedOptions: string[]) => void;
  applyChange: () => void;
  selectedSerials: string[];
}

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  options = [],
  selectedOptions = [],
  onChange,
  applyChange,
  selectedSerials = [],
}) => {
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [sortedOptions, setSortedOptions] = useState<{ option: string; name: string }[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);

  // Memoize filtered and sorted options
  const filteredSortedOptions = useMemo(() => {
    const filteredOptions = options.filter(option => !option.includes("###SERIAL###"));
    const optionsWithNames = filteredOptions.map(option => ({
      option,
      name: findHost(option),
    }));

    const sortedDifferentNameOptions = optionsWithNames
      .filter(({ option, name }) => option !== name)
      .sort((a, b) => a.name.localeCompare(b.name));
    const sortedSameNameOptions = optionsWithNames
      .filter(({ option, name }) => option === name)
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...sortedDifferentNameOptions, ...sortedSameNameOptions];
  }, [options]);

  // Update sorted options whenever modal opens or options change
  useEffect(() => {
    if (isOpen) {
      setSortedOptions(filteredSortedOptions);
      setSelectAllChecked(selectedSerials.length === options.length);
  
      // Adjust position to prevent it from exiting the viewport
      if (modalRef.current) {
        const rect = modalRef.current.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          modalRef.current.style.left = `-${rect.right - window.innerWidth + 16}px`; // Shift left to fit
        }
      }
    }
  }, [isOpen, filteredSortedOptions, selectedSerials, options.length]);
  // Handle clicks outside of the modal to close it
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !event.defaultPrevented
      ) {
        onClose();
      }
    },
    [onClose]
  );
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, handleOutsideClick]);

  const handleCheckboxChange = (option: string) => {
    const updatedSelection = selectedOptions.includes(option)
      ? selectedOptions.filter(item => item !== option)
      : [option];
    onChange(updatedSelection);
    if (updatedSelection.length === 1) setSelectAllChecked(false);
  };

  const handleSelectAll = () => {
    const newSelection = selectAllChecked
      ? []
      : options.filter(option => !option.includes("###SERIAL###"));
    onChange(newSelection);
    setSelectAllChecked(!selectAllChecked);
  };

  return (
    <>
      {isOpen && (
        <div className={classes.FilterWindow} ref={modalRef}>
          <div className={classes.ModalContent}>
            <span className={classes.ModalClose} onClick={onClose}>
              &times;
            </span>
            <h2 className={classes.ModalTitle}>{i18n.t("SelectDevice")} :</h2>
            <div className={classes.ModalOption}>
              <input
                type="checkbox"
                id="selectAll"
                checked={selectAllChecked}
                onChange={handleSelectAll}
              />
              <label className={classes.ModalTitleOption} htmlFor="selectAll">
                {i18n.t("SelectAll")}
              </label>
            </div>
            {sortedOptions.map(({ option, name }) => (
              <div key={option} className={classes.ModalOption}>
                <input
                  type="checkbox"
                  id={option}
                  value={option}
                  checked={selectedOptions.includes(option)}
                  onChange={() => handleCheckboxChange(option)}
                  disabled={selectAllChecked}
                />
                <label className={classes.ModalTitleOption} htmlFor={option}>
                  {name}
                </label>
              </div>
            ))}
          </div>
          <button className={classes.ModalApply} onClick={applyChange}>
            {i18n.t("ApplyFilters")}
          </button>
        </div>
      )}
    </>
  );
};
